import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useImperativeHandle,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'usehooks-ts';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import _debounce from 'lodash/debounce';
import { FieldItem, Values } from '@lib/interfaces/form';
import TabBar from '@lib/components/TabBar/TabBar';
import Table from '@lib/components/Table/Table';
import Form from '@lib/components/ReactHookForm/FormContainer';
import Modal from '@lib/components/Modal/Modal';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import formStyles from '@lib/assets/styles/Form.module.scss';
import { BELOW_LG1_QUERY } from '@lib/assets/styles/breakpoints';
import { useTableItemsBulkSelectWithAllIds } from '@lib/hooks/useTableItemsBulkSelect';
import useTableItemsToggleColumn from '@lib/hooks/useTableItemsToggleColumn';
import Search from '@lib/components/Search/Search';
import { BaseItem, Column, TableTreeProps } from '@lib/components/Table/types';
import tableItemsBulkAction from '@lib/utils/tableItemsBulkAction';
import { FetchIdsResponse } from '@lib/utils/getAllTableItemsIds';
import {
  OrderingState,
  PaginationState,
  SearchStringState,
} from '@lib/enums/urls';
import buildVisibleViaQrColumn from '@lib/components/Table/utils/buildVisibleViaQrColumn';
import ModalTableEmptyState from './ModalTableEmptyState';
import TableMultiselectFilterActions from './TableMultiselectFilterActions';
import toModalFilterField from './utils/toModalFilterField';
import getTableTabs from './utils/getTableTabs';
import { TableTabs } from './enums';
import {
  FilterState,
  ActiveTabState,
} from './hooks/useModalTableMultiselectStates';
import styles from './ModalTableMultiselect.module.scss';

const isEmptyFilterValue = (value: unknown) =>
  value == null || value === '' || (Array.isArray(value) && !value.length);

/**
 * The picker body only — tabs, search, filter row and table. It deliberately
 * owns no chrome: no `Modal`, no Cancel/Confirm buttons and no notion of
 * "closing". The container decides where the body lives (a standalone modal
 * via `ModalTableMultiselect`, or one step of a multi-step modal) and renders
 * the actions itself, driving confirmation through `TableMultiselectHandle`.
 */
export interface TableMultiselectHandle {
  /**
   * Same path the standalone modal's "Confirm selection" button takes:
   * resolves the selected ids (fetching every id when "select all" is on),
   * emits `onSelect`, clears bulk-selection state, then calls
   * `onSelectionConfirmed`. Asynchronous in the select-all case, which is why
   * "what happens next" is a callback rather than the caller's own next line.
   */
  confirmSelection: () => void;
  /** Drops bulk-selection state without emitting `onSelect` — the cancel path. */
  clearSelection: () => void;
}

/** Content padding override the picker body expects from its `Modal` host. */
export const tableMultiselectModalContentClassName =
  styles.modalContentClassName;

export interface VisibleViaQrColumnConfig<T extends BaseItem> {
  enabled?: boolean;
  defaultValue?: boolean;
  getInitialValue?: (item: T) => boolean | undefined;
  onCommitRow?: (item: T, value: boolean) => void;
  onCommitMany?: (ids: string[], value: boolean) => void;
  initialVisibilityMap?: Record<string, boolean>;
  onVisibilityMapChange?: (next: Record<string, boolean>) => void;
}

export interface TableMultiselectProps<T extends BaseItem> {
  activeTabState: ActiveTabState;
  columns: Column<T>[];
  data: T[];
  dataSelected: T[];
  /**
   * Overrides the [title, text] i18n keys shown for the "All"/default empty
   * state — e.g. `['no-task-reporting-yet', 'no-task-reporting-text']` for a
   * picker whose entity isn't generic "data". See `ModalTableEmptyState`.
   */
  emptyStateTextKeys?: [string, string];
  fetchIds: () => Promise<FetchIdsResponse<unknown>>;
  filterFormFields: FieldItem[];
  filterState: FilterState;
  filterStateSelected: FilterState;
  /**
   * The filter values a Reset should return to — the empty base shape with
   * any `filterOverrides` folded in, so a locked/prefilled field resets to
   * its forced value instead of empty. Also used to tell a genuinely
   * user-applied filter apart from a permanently-set prefilled one, so the
   * empty state doesn't read "filtered" just because a locked field has a
   * value.
   */
  filterStateDefault: Values;
  getIsRowDisabled?: (item: T) => boolean;
  /**
   * Rendered above the tab bar — e.g. a summary of the entity whose items
   * are being picked (see `TaskCompletionsTableMultiselect`).
   */
  headerContent?: React.ReactNode;
  /**
   * Hides the free-text search input — for a picker whose entity has no
   * backing search field (e.g. TaskCompletion), so the input doesn't imply a
   * filtering capability that silently does nothing.
   */
  hideSearch?: boolean;
  /**
   * Whether the body is currently on screen. Drives the one-shot
   * initialization of the incoming `selected` ids, so a container that keeps
   * the body mounted while hidden still gets a clean re-initialization when
   * it comes back.
   */
  isOpen: boolean;
  /**
   * A filter that lives outside the filter form values (e.g. Documents'
   * Favorites toggle) but should still count as "a filter is active" for
   * the counter, the Reset control and the filtered empty state.
   */
  isExtraFilterActive?: boolean;
  loading?: boolean;
  loadingSelected?: boolean;
  onIsFetchingAllIdsChange?: (isFetching: boolean) => void;
  /** Resets the active (and Selected) tab's filters back to `filterStateDefault`. */
  onResetFilter: () => void;
  onSelect: (
    values: string[],
    sites: Values[],
    visibilityMap?: Record<string, boolean>,
  ) => void;
  /** Fired after a `confirmSelection()` run has emitted `onSelect`. */
  onSelectionConfirmed?: () => void;
  onSelectedIdsChange?: (ids: string[]) => void;
  orderingState: OrderingState;
  orderingStateSelected: OrderingState;
  paginationState: PaginationState;
  paginationStateSelected: PaginationState;
  radioSelect?: boolean;
  ref?: React.Ref<TableMultiselectHandle>;
  searchPleceholder: string;
  searchState: SearchStringState;
  searchStateSelected: SearchStringState;
  selected?: string[];
  selectionLimit?: number;
  firstLoading?: boolean;
  firstLoadingSelected?: boolean;
  totalEntries: number;
  totalEntriesSelected: number;
  /**
   * Renders the All tab as an expandable hierarchy (e.g. Departments).
   * Intentionally not applied to the Selected tab — once picked, a flat
   * list of exactly what's linked is more useful than hunting through the
   * hierarchy (mirrors InterventionDepartmentsModal's read-only precedent).
   */
  tree?: TableTreeProps<T>;
  visibleViaQrColumnConfig?: VisibleViaQrColumnConfig<T>;
}

/** Trailing Reset button appended to the inline filter row while a filter is active. */
const getResetFilterField = (
  buttonText: string,
  onReset: () => void,
): FieldItem => ({
  name: 'tableMultiselectFilterReset',
  label: '',
  customRender: () => (
    <Button
      buttonSize={ButtonSizes.large}
      buttonText={buttonText}
      buttonType={ButtonTypes.primaryText}
      onClick={onReset}
    />
  ),
  formItemClassName: formStyles.formColumnAuto,
  hideErrorsSpace: true,
  isOptionalFn: () => true,
});

function TableMultiselect<T extends BaseItem>({
  activeTabState,
  columns,
  data,
  dataSelected,
  emptyStateTextKeys,
  fetchIds,
  filterFormFields,
  filterState,
  filterStateSelected,
  filterStateDefault,
  getIsRowDisabled,
  headerContent,
  hideSearch,
  firstLoading,
  firstLoadingSelected,
  isOpen,
  isExtraFilterActive = false,
  loading = false,
  loadingSelected = false,
  onIsFetchingAllIdsChange,
  onResetFilter,
  onSelect,
  onSelectionConfirmed,
  onSelectedIdsChange,
  orderingState,
  orderingStateSelected,
  paginationState,
  paginationStateSelected,
  radioSelect,
  ref,
  searchPleceholder,
  searchState,
  searchStateSelected,
  selected,
  selectionLimit,
  totalEntries,
  totalEntriesSelected,
  tree,
  visibleViaQrColumnConfig,
}: TableMultiselectProps<T>) {
  const { t } = useTranslation();

  const [searchValue, onSearchHandler] = searchState;
  const [searchValueSelected, onSearchHandlerSelected] = searchStateSelected;
  const [activeTab, setActiveTab] = activeTabState;

  const useBulkSelectResult = useTableItemsBulkSelectWithAllIds<T>({
    fetchIds,
  });

  const visibleViaQrEnabled = !!visibleViaQrColumnConfig?.enabled;
  const fetchAllSelectedIdsForVisibleViaQr = useCallback(async () => {
    const response = await fetchIds();
    const collection = (response?.data?.data?.collection ?? []) as Array<{
      id: string | number;
    }>;
    return collection.map((item) => String(item.id));
  }, [fetchIds]);

  const {
    handleUnselectAllTableItems,
    initializeSelectedItems,
    selectedTableItems,
    getSelectedItemsCount,
    isResolvingAllItemIds,
    areAllItemsMaterialized,
  } = useBulkSelectResult;

  const selectedIds = useMemo(
    () => selectedTableItems.map((item) => String(item.id)),
    [selectedTableItems],
  );

  useEffect(() => {
    if (onIsFetchingAllIdsChange) {
      onIsFetchingAllIdsChange(isResolvingAllItemIds);
    }
  }, [isResolvingAllItemIds, onIsFetchingAllIdsChange]);

  useEffect(() => {
    if (isOpen && onSelectedIdsChange) {
      onSelectedIdsChange(selectedIds);
    }
  }, [isOpen, onSelectedIdsChange, selectedIds]);

  // Seed the selection from `selected` each time the modal opens — during
  // render, so the first paint already shows the right checkboxes. Partial
  // `{ id }` items are enough: full rows come from `data` (All tab) and
  // `dataSelected` (Selected tab); bulk selection only needs the ids.
  const wasOpenRef = useRef(false);
  if (wasOpenRef.current !== isOpen) {
    wasOpenRef.current = isOpen;
    if (isOpen && selected?.length) {
      initializeSelectedItems(selected.map((id) => ({ id }) as T));
    }
  }

  const handleTabChange = (value: TableTabs) => {
    setActiveTab(value);
  };

  const [filterValues, setFilterValues] = filterState;
  const [filterValuesSelected, setFilterValuesSelected] = filterStateSelected;

  // Calculate selected counter using the most accurate total available.
  // Once the hook materializes every id we pivot to the enumerated list; otherwise we fall back to totals.
  const selectedCounterBase = areAllItemsMaterialized
    ? selectedTableItems.length
    : totalEntriesSelected || totalEntries;
  const selectedCounter = getSelectedItemsCount(selectedCounterBase);
  // Single-select pickers show only the All tab (the hooks default to it).
  const isSingleSelect = selectionLimit === 1;
  const tabItems = getTableTabs({
    t,
    allCounter: totalEntries,
    selectedCounter,
  });

  // Determine which data, pagination, search, and ordering to use based on active tab
  const isSelectedTab = activeTab === TableTabs.Selected;
  const displayData = isSelectedTab ? dataSelected : data;
  const displayTree = isSelectedTab ? undefined : tree;
  const displayLoading = isSelectedTab ? loadingSelected : loading;
  const displayFirstLoading = isSelectedTab
    ? firstLoadingSelected
    : firstLoading;
  const displayTotalEntries = isSelectedTab
    ? totalEntriesSelected
    : totalEntries;
  const displayPaginationState = isSelectedTab
    ? paginationStateSelected
    : paginationState;
  const displaySearchValue = isSelectedTab ? searchValueSelected : searchValue;
  const displaySearchHandler = isSelectedTab
    ? onSearchHandlerSelected
    : onSearchHandler;
  const displayOrderingState = isSelectedTab
    ? orderingStateSelected
    : orderingState;

  const visibleViaQrColumnResult = useTableItemsToggleColumn<T>({
    enabled: visibleViaQrEnabled,
    data: isSelectedTab ? dataSelected : data,
    useBulkSelectResult,
    defaultValue: visibleViaQrColumnConfig?.defaultValue ?? true,
    getInitialValue: visibleViaQrColumnConfig?.getInitialValue,
    fetchAllSelectedIds: fetchAllSelectedIdsForVisibleViaQr,
    onCommitRow: visibleViaQrColumnConfig?.onCommitRow,
    onCommitMany: visibleViaQrColumnConfig?.onCommitMany,
  });

  const displayColumns = useMemo<Column<T>[]>(() => {
    if (!visibleViaQrEnabled) return columns;
    return [buildVisibleViaQrColumn(visibleViaQrColumnResult, t), ...columns];
  }, [columns, visibleViaQrEnabled, visibleViaQrColumnResult, t]);

  // Restore caller-provided visibility map on modal open (for create-mode persistence).
  useEffect(() => {
    if (
      visibleViaQrEnabled &&
      isOpen &&
      visibleViaQrColumnConfig?.initialVisibilityMap
    ) {
      visibleViaQrColumnResult.setVisibilityMap(
        visibleViaQrColumnConfig.initialVisibilityMap,
      );
    }
    // Intentionally only react to modal open transitions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, visibleViaQrEnabled]);

  // Notify caller whenever the visibility map changes.
  useEffect(() => {
    if (visibleViaQrEnabled) {
      visibleViaQrColumnConfig?.onVisibilityMapChange?.(
        visibleViaQrColumnResult.visibilityMap,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleViaQrColumnResult.visibilityMap, visibleViaQrEnabled]);

  const displayFilterValues = isSelectedTab
    ? filterValuesSelected
    : filterValues;

  // Compare against filterStateDefault, not just "is this field non-empty" —
  // a locked/prefilled field (e.g. a pinned Site) is permanently non-empty
  // by design and shouldn't count as a user-applied filter. All empty shapes
  // (`[]`, `''`, `null`, absent) count as equal, so a cleared field whose
  // default omits it (e.g. `filterStateDefault={}`) isn't counted.
  const filterCounter =
    Object.keys({
      ...filterStateDefault,
      ...displayFilterValues,
    }).reduce((count, key) => {
      const value = _get(displayFilterValues, key);
      const defaultValue = _get(filterStateDefault, key);
      const isFieldFiltered =
        !(isEmptyFilterValue(value) && isEmptyFilterValue(defaultValue)) &&
        !_isEqual(value, defaultValue);
      return isFieldFiltered ? count + 1 : count;
    }, 0) + (isExtraFilterActive ? 1 : 0);

  // Below $lg1 the inline filter row doesn't fit, so the fields move into a
  // nested filter modal opened from a Filter chip (applied on submit rather
  // than on change), and Reset shrinks to an icon next to the chip.
  const isSmallScreen = useMediaQuery(BELOW_LG1_QUERY);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const hasFilterFields = filterFormFields.length > 0;

  const inlineFilterFields = useMemo<FieldItem[]>(
    () =>
      filterCounter > 0
        ? [...filterFormFields, getResetFilterField(t('reset'), onResetFilter)]
        : filterFormFields,
    [filterCounter, filterFormFields, onResetFilter, t],
  );

  const modalFilterFields = useMemo(
    () => filterFormFields.map(toModalFilterField),
    [filterFormFields],
  );

  // Actions
  // Debounce filter form submissions to reduce rapid state updates on change
  const debouncedFilterSubmit = useMemo(
    () =>
      _debounce((values: Values, tab: TableTabs) => {
        if (tab === TableTabs.Selected) {
          setFilterValuesSelected(values);
        } else {
          setFilterValues(values);
        }
      }, 300),
    [setFilterValues, setFilterValuesSelected],
  );

  useEffect(() => {
    return () => debouncedFilterSubmit.cancel();
  }, [debouncedFilterSubmit]);

  const handleFilterFormSubmit = useCallback(
    (values: Values) => {
      debouncedFilterSubmit(values, activeTab);
    },
    [debouncedFilterSubmit, activeTab],
  );

  const closeFilterModal = () => setIsFilterModalOpen(false);

  const handleFilterModalSubmit = (values: Values) => {
    if (activeTab === TableTabs.Selected) {
      setFilterValuesSelected(values);
    } else {
      setFilterValues(values);
    }
    closeFilterModal();
  };

  const handleFilterModalClear = () => {
    onResetFilter();
    closeFilterModal();
  };

  const handleResetSearch = () => {
    if (activeTab === TableTabs.Selected) {
      onSearchHandlerSelected('');
    } else {
      onSearchHandler('');
    }
  };

  const onGetAllIds = useCallback(
    () => Promise.resolve(selectedIds),
    [selectedIds],
  );

  useImperativeHandle(
    ref,
    () => ({
      clearSelection: handleUnselectAllTableItems,
      confirmSelection: () => {
        // Note: fetchIds will be called by tableItemsBulkAction via onGetAllIds if needed
        tableItemsBulkAction<T>({
          action: (idsValues?: string[]) => {
            onSelect(
              idsValues || [],
              _get(displayFilterValues, 'sites') as Values[],
              visibleViaQrEnabled
                ? visibleViaQrColumnResult.visibilityMap
                : undefined,
            );
            handleUnselectAllTableItems();
            onSelectionConfirmed?.();
          },
          useBulkSelectResult,
          onGetAllIds,
        });
      },
    }),
    [
      displayFilterValues,
      handleUnselectAllTableItems,
      onGetAllIds,
      onSelect,
      onSelectionConfirmed,
      useBulkSelectResult,
      visibleViaQrColumnResult.visibilityMap,
      visibleViaQrEnabled,
    ],
  );

  const isContentVisible = !!displayTotalEntries && !!displayData.length;
  const isEmptyStateVisible = !isContentVisible && !displayFirstLoading;
  const isSearchApplied = !!displaySearchValue;
  const isFilterApplied = !!filterCounter;
  const isEmptyState =
    !displayLoading &&
    isEmptyStateVisible &&
    !isSearchApplied &&
    !isFilterApplied;
  const isEmptyStateBySearch = isEmptyStateVisible && isSearchApplied;
  const isEmptyStateByFilter = isEmptyStateVisible && isFilterApplied;
  const emptyState = (
    <ModalTableEmptyState
      isEmptyState={isEmptyState}
      isEmptyStateBySearch={isEmptyStateBySearch}
      isEmptyStateByFilter={isEmptyStateByFilter}
      tabParam={activeTab}
      onResetFilter={onResetFilter}
      onResetSearch={handleResetSearch}
      emptyStateTextKeys={emptyStateTextKeys}
    />
  );

  return (
    <div>
      {headerContent}
      {!isSingleSelect && (
        <TabBar
          items={tabItems}
          active={activeTab}
          onActiveChange={({ value }) => handleTabChange(value as TableTabs)}
          useSidePaddings={false}
        />
      )}
      {!hideSearch && (
        <Search
          key={`search-${activeTab}`}
          placeholder={searchPleceholder}
          useSearchParam={false}
          value={displaySearchValue}
          onChange={displaySearchHandler}
        />
      )}
      {hasFilterFields && isSmallScreen && (
        <>
          <div className={styles.filterChipRow}>
            <Chip
              counter={filterCounter}
              label={t('filter')}
              leftIcon="filter_alt"
              leftIconSymbolsOutlined
              onClick={() => setIsFilterModalOpen(true)}
              size={ChipSizes.large}
              type={ChipTypes.chipFilter}
              tooltipForCroppedTextDisabled
            />
            {filterCounter > 0 && (
              <IconButton
                type={IconButtonTypes.TertiaryFilled}
                icon="cancel"
                onClick={onResetFilter}
              />
            )}
          </div>
          <Modal
            isOpen={isFilterModalOpen}
            caption={t('filter')}
            onBack={closeFilterModal}
            onClose={closeFilterModal}
          >
            <Form
              key={activeTab}
              fields={modalFilterFields}
              defaultValues={displayFilterValues}
              formId="modal-table-filter-modal"
              onSubmit={handleFilterModalSubmit}
              actionsComponent={TableMultiselectFilterActions}
              actionsComponentProps={{ onClear: handleFilterModalClear }}
              formClassName={formStyles.form}
            />
          </Modal>
        </>
      )}
      {!isSmallScreen && (
        <div className={styles.filterWrap}>
          <Form
            fields={inlineFilterFields}
            defaultValues={displayFilterValues}
            formId="modal-table-filter"
            onSubmit={handleFilterFormSubmit}
            submitOnChange
            formItemsClassName={styles.formItemsClassName}
            preserveDirtyOnDefaultValuesChange={false}
          />
        </div>
      )}
      <Table<T>
        columns={displayColumns}
        data={displayData}
        emptyState={emptyState}
        getIsRowDisabled={getIsRowDisabled}
        hasSelectColumn
        loading={displayLoading}
        orderingState={displayOrderingState}
        paginationState={displayPaginationState}
        radioSelect={radioSelect}
        selectionLimit={selectionLimit}
        totalEntries={displayTotalEntries}
        tree={displayTree}
        useBulkSelectResult={useBulkSelectResult}
      />
    </div>
  );
}

// Not wrapped in `memo`: hosts pass `[value, setter]` state tuples and inline
// callbacks, which are fresh every render, so a shallow compare never skips.
export default TableMultiselect;
